import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { DataChangeEmitPayload, DataChangeEmitWhat, DataChangeEmitWho } from '../models/data-change-emit/data-change-emit-payload';
import { NotableActivityOption } from '../entities/NotableActivityOption';
import { DataService } from './data.service';
import { Callback, CallbackNames } from '../models/db/db-transaction-callback';
import { DbTransaction } from '../models/db/db-transaction';
import { StoreName } from '../entities/StoreName';
import { RecordNotFoundException, RecordSoftDeletedException } from '../utils/exceptions';
import { GlobalConstants } from '../constant/constant';
import { Check, EntityHelper } from '../utils/entity-helper';

@Injectable({
  providedIn: 'root',
})
export class NotableActivityOptionService extends DataService {
  private emitChangeSource = new Subject<DataChangeEmitPayload<NotableActivityOption>>();
  changeEmitted$ = this.emitChangeSource.asObservable();

  constructor() {
    super();
  }

  // #region Utility

  emitChange(notableActivityOption: NotableActivityOption, who: DataChangeEmitWho, what: DataChangeEmitWhat) {
    this.emitChangeSource.next({ data: notableActivityOption, who: who, what: what });
  }

  getEmitChangeCallback(notableActivityOption: NotableActivityOption, who: DataChangeEmitWho, what: DataChangeEmitWhat): Callback {
    return {
      name: CallbackNames.EmitChangeForNotableActivity + '_' + notableActivityOption.RecordId,
      fn: this.emitChange.bind(this, notableActivityOption, who, what),
    };
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.NotableActivityOption], 'readwrite'));
  }

  // #endregion
  // #region Low-level Get One

  async getNotableActivityOptionByOnlineId(onlineId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<NotableActivityOption> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByOnlineId<NotableActivityOption>(StoreName.NotableActivityOption, onlineId, NotableActivityOption, transaction);

    if (result === null) {
      throw new RecordNotFoundException(onlineId);
    }

    const record = result as NotableActivityOption;

    if (this.checkIsNotableActivityOptionSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(onlineId);
    }

    return record;
  }

  async getNotableActivityOptionByRecordId(recordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<NotableActivityOption> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<NotableActivityOption>(StoreName.NotableActivityOption, recordId, NotableActivityOption, transaction);

    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as NotableActivityOption;

    if (this.checkIsNotableActivityOptionSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(recordId);
    }

    return record;
  }

  // #endregion
  // #region Low-level Get Many

  async getAllNotableActivityOptions(filter: any = undefined, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<NotableActivityOption[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAll<NotableActivityOption>(StoreName.NotableActivityOption, filter, NotableActivityOption, transaction);

    let records = result as NotableActivityOption[];

    if (!includeDeleted) {
      records = records.filter(notableActivityOption => !this.checkIsNotableActivityOptionSoftDeleted(notableActivityOption));
    }

    return records;
  }

  async getNotableActivityOptionsByOnlineIds(onlineIds: number[], existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<NotableActivityOption[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<NotableActivityOption>(StoreName.NotableActivityOption, GlobalConstants.KEY_NAMES.Id, onlineIds, NotableActivityOption, transaction);

    let records = result as NotableActivityOption[];

    if (!includeDeleted) {
      records = result.filter(notableActivityOption => !this.checkIsNotableActivityOptionSoftDeleted(notableActivityOption));
    }

    return records;
  }

  async getNotableActivityOptionsByProjectId(projectId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<NotableActivityOption[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    let result = await this.repository.getAllByIndex<NotableActivityOption>(StoreName.NotableActivityOption, GlobalConstants.KEY_NAMES.ProjectId, projectId, NotableActivityOption, transaction);

    if (!includeDeleted) {
      result = result.filter(notableActivityOption => !this.checkIsNotableActivityOptionSoftDeleted(notableActivityOption));
    }

    return result;
  }

  async getNotableActivityOptionsByLaborCategoryId(laborCategoryId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<NotableActivityOption[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    let result = await this.repository.getAllByIndex<NotableActivityOption>(
      StoreName.NotableActivityOption,
      GlobalConstants.KEY_NAMES.LaborCategoryId,
      laborCategoryId,
      NotableActivityOption,
      transaction
    );

    if (!includeDeleted) {
      result = result.filter(notableActivityOption => !this.checkIsNotableActivityOptionSoftDeleted(notableActivityOption));
    }

    return result;
  }

  // #endregion
  // region Low-level Create

  private async createNotableActivityOption(who: DataChangeEmitWho, recordToCreate: NotableActivityOption, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LC1. Validate action
    await this.validateNotableActivityOptionCreation(transaction);

    // TODO: Impelment LC2 and LC3

    // LC4. Repository call
    const recordId = await this.repository.create(recordToCreate, StoreName.NotableActivityOption, transaction);

    // LC5. Get created record
    const createdRecord = await this.getNotableActivityOptionByRecordId(recordId, transaction, includeDeleted);

    // LC6. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(createdRecord, who, DataChangeEmitWhat.Create));

    // LC7. Return id
    return recordId;
  }

  // #endregion
  // region Low-level Update

  async updateNotableActivityOption(
    who: DataChangeEmitWho,
    recordToUpdate: NotableActivityOption,
    skipLocalCopyValidation: boolean,
    localCopy: NotableActivityOption | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LU1. Get existing record
    const existingRecord = await this.getNotableActivityOptionByRecordId(recordToUpdate.RecordId, transaction);

    // LU2. Validate action
    await this.validateNotableActivityOptionUpdate(recordToUpdate, transaction);

    // LU3. Idempotent check
    if (EntityHelper.checkAllFieldsEqual(existingRecord, recordToUpdate)) {
      return;
    }

    // LU4. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<NotableActivityOption>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy<NotableActivityOption>(localCopy, existingRecord, checks);
    }

    // LU5. Validate model
    await this.validateNotableActivityOption(recordToUpdate, transaction);

    // LU6. Repository call
    await this.repository.update(recordToUpdate, StoreName.NotableActivityOption, transaction);

    // LU7. Get updated record
    const updatedRecord = await this.getNotableActivityOptionByRecordId(recordToUpdate.RecordId, transaction);

    // LU8. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(updatedRecord, who, DataChangeEmitWhat.Update));

    // LU9. Return
    return;
  }

  // #endregion
  // region Low-level Delete

  // #endregion
  // region Low-level Validate Model

  async validateNotableActivityOption(notableActivityOption: NotableActivityOption, existingTransaction?: DbTransaction): Promise<void> {
    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Low-level Validate Action

  async validateNotableActivityOptionCreation(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  async validateNotableActivityOptionUpdate(notableActivityOption: NotableActivityOption, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Mid-level Get

  // #endregion
  // region Mid-level Create

  // #endregion
  // region Mid-level Update

  // #endregion
  // region Mid-level Delete

  // #endregion
  // region Mid-level Validate

  // #endregion
  // region Mid-level Upsert

  // #endregion
  // region Mid-level Sync

  async upsertNotableActivityOptionFromSync(who: DataChangeEmitWho, recordToUpsert: NotableActivityOption, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    try {
      const existingRecord = await this.getNotableActivityOptionByOnlineId(recordToUpsert.Id, transaction, true);
      recordToUpsert.RecordId = existingRecord.RecordId;
      await this.updateNotableActivityOption(who, recordToUpsert, true, null, transaction);
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        await this.createNotableActivityOption(who, recordToUpsert, transaction, true);
      }
    }

    return;
  }

  // #endregion
  // region Check

  private checkIsNotableActivityOptionSoftDeleted(record: NotableActivityOption): boolean {
    return record.IsDeleted;
  }

  private checkImmutableFieldsMatch(a: NotableActivityOption, b: NotableActivityOption): boolean {
    return a.Name === b.Name && a.ProjectId === b.ProjectId && a.LaborCategoryId === b.LaborCategoryId;
  }

  // #endregion
  // region Lookup

  // #endregion
  // region Transform

  // #endregion
  // region Other

  // #endregion
}
